import { List, Record } from 'immutable';
import {
  checkStakeValueMultiple,
  checkStakeValueSingle,
  isAmountDecimalOrEmpty
} from '../../modules/validators';
import {
  LOAD_SLIPS_FAILED,
  LOAD_SLIPS_START,
  LOAD_SLIPS_SUCCESS,
  SET_EVENTS_DAY,
  SET_MARKET_GROUP_ID,
  SLIP_CLEAR,
  SLIP_NAME_CHANGED,
  SLIP_RESTORE,
  SLIP_SAVE_PENDING,
  SLIP_SAVED,
  SLIP_SELECTION_ADD,
  SLIP_SELECTION_DESELECT,
  SLIP_SELECTION_REMOVE,
  SLIP_STAKE_CHANGED
} from './actions';
import { slipNameValidator } from './validators';

export const MAXIMUM_NUMBER_OF_SLIP_LEGS = 20;
export const MARKET_GROUP_90_MINUTES = '-804892490';
export const MARKET_GROUP_BOTH_TEAMS_TO_SCORE = '2043448489';
export const MARKET_GROUP_OVER_UNDER_X_GOALS = '2113444268';
export const MARKET_GROUP_RESULT_AND_BOTH_TEAMS_TO_SCORE = '-770947707';

export const Slip = new Record({
  id: '',
  slipName: '',
  slipStake: '1',
  slipCreatedDateTime: '',
  expiryDateTime: '',
  legs: new List(),
  potentialReturns: 0,
  odds: ''
});

export const SlipLeg = new Record({
  selectionId: '',
  selectionName: '',
  selectionPriceNum: '',
  selectionPriceDen: '',
  marketId: '',
  marketName: '',
  eventId: '',
  eventName: '',
  eventStartDateTime: ''
});

export const InitialState = new Record({
  currentSlip: new Slip(),
  legsLimitExceeded: false,
  eventsDay: 0,
  marketGroupId: MARKET_GROUP_90_MINUTES,
  savedSlips: new List(),
  slipSavePending: false,
  currentSlipStakeErrors: new List(),
  savedSlipsViewIsLoading: false
});

const slipStakeChangeReducer = (state, action) => {
  const { stake } = action;
  const isDecimalStake = isAmountDecimalOrEmpty(stake);

  if (!isDecimalStake) {
    return state;
  }

  const isSingle = state.getIn(['currentSlip', 'legs']).size === 1;
  const stakeValueErrors = isSingle ? checkStakeValueSingle(stake) : checkStakeValueMultiple(stake);

  if (stakeValueErrors.length) {
    return state
      .setIn(['currentSlip', 'slipStake'], stake)
      .set('currentSlipStakeErrors', new List([stakeValueErrors[0]]));
  }

  return state
    .setIn(['currentSlip', 'slipStake'], stake)
    .delete('currentSlipStakeErrors');
};

const slipSelectionAddReducer = (state, action) => {
  const {
    selection: {
      selectionId,
      selectionName,
      selectionPriceNum,
      selectionPriceDen,
      marketId,
      marketName,
      eventId,
      eventName,
      eventStartDateTime
    }
  } = action;

  const oldIndex = state.getIn(['currentSlip', 'legs'])
    .findIndex(l => l.eventId === eventId && l.marketId === marketId);
  const index = oldIndex > -1 ? oldIndex : state.getIn(['currentSlip', 'legs']).size;
  if (index === MAXIMUM_NUMBER_OF_SLIP_LEGS) {
    return state.set('legsLimitExceeded', true);
  }
  return slipStakeChangeReducer(state
    .set('legsLimitExceeded', false)
    .setIn(['currentSlip', 'legs', index], new SlipLeg({
      selectionId,
      selectionName,
      selectionPriceNum,
      selectionPriceDen,
      marketId,
      marketName,
      eventId,
      eventName,
      eventStartDateTime
    })), { stake: state.getIn(['currentSlip', 'slipStake']) });
};

const slipSelectionRemoveReducer = (state, { selectionId }) => {
  const legs = state.getIn(['currentSlip', 'legs']);
  const index = legs.findIndex(l => l.selectionId === selectionId);
  if (index === -1) {
    return state;
  }
  if (legs.size === 1) {
    return state
      .set('currentSlip', new Slip())
      .set('currentSlipStakeErrors', new List());
  }
  return slipStakeChangeReducer(
    state
      .set('legsLimitExceeded', false)
      .deleteIn(['currentSlip', 'legs', index]),
    { stake: state.getIn(['currentSlip', 'slipStake']) }
  );
};

export default (state = new InitialState(), action) => {
  const { type } = action;
  switch (type) {
    case SET_EVENTS_DAY: {
      return state.set('eventsDay', action.day);
    }
    case SET_MARKET_GROUP_ID: {
      return state.set('marketGroupId', action.marketGroupId);
    }
    case SLIP_SELECTION_ADD: {
      return slipSelectionAddReducer(state, action);
    }
    case SLIP_SELECTION_DESELECT: {
      const { selection: { selectionId } } = action;
      return slipSelectionRemoveReducer(state, { selectionId });
    }
    case SLIP_SELECTION_REMOVE: {
      return slipSelectionRemoveReducer(state, action);
    }
    case SLIP_SAVED:
    case SLIP_CLEAR: {
      return state.merge({
        legsLimitExceeded: false,
        currentSlip: new Slip(),
        currentSlipStakeErrors: new List()
      });
    }
    case SLIP_RESTORE: {
      const {
        slipName,
        legs = [],
        slipStake
      } = action.slip;
      return slipStakeChangeReducer(state.set('currentSlip', new Slip({
        slipName,
        slipStake,
        legs: new List(legs.map(l => new SlipLeg(l)))
      })), { stake: slipStake });
    }
    case SLIP_NAME_CHANGED: {
      return slipNameValidator(action.name)
        ? state.setIn(['currentSlip', 'slipName'], action.name)
        : state;
    }
    case SLIP_STAKE_CHANGED: {
      return slipStakeChangeReducer(state, action);
    }
    case LOAD_SLIPS_SUCCESS: {
      return state.set('savedSlips', new List(action.slips.map(slip => new Slip({
        id: slip.id,
        slipName: slip.name,
        slipStake: Number(slip.stake / 100).toFixed(2),
        slipCreatedDateTime: slip.createdDateTime,
        expiryDateTime: slip.expiryDateTime,
        legs: new List(slip.selections.map(selection => new SlipLeg({
          selectionId: selection.selectionId,
          selectionName: selection.selectionName,
          selectionPriceNum: parseInt(selection.price.split('/')[0], 10),
          selectionPriceDen: parseInt(selection.price.split('/')[1], 10),
          eventId: selection.eventId,
          eventName: selection.eventName,
          eventStartDateTime: selection.startDateTime,
          marketName: selection.marketName,
          price: selection.price
        })))
      }))))
        .set('savedSlipsViewIsLoading', false);
    }
    case LOAD_SLIPS_START: {
      return state.set('savedSlipsViewIsLoading', true);
    }
    case LOAD_SLIPS_FAILED: {
      return state.set('savedSlipsViewIsLoading', false);
    }
    case SLIP_SAVE_PENDING: {
      return state.set('slipSavePending', action.isPending);
    }
    default: {
      return state;
    }
  }
};
